import { useState } from "react";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { COLORS } from "./lib/colors";
import * as Sentry from "@sentry/react";
import LoginPage from "./components/LoginPage";
import AppShell from "./components/AppShell";
import DemoSwitcher from "./components/DemoSwitcher";
import SchoolSignup from "./components/SchoolSignup";
import SchoolOnboarding from "./components/SchoolOnboarding";

/**
 * Detect ?signup or #signup in the URL to show the self-serve school registration flow.
 * Also detect ?onboarding_complete=1 to re-show the onboarding wizard post-Stripe redirect.
 */
function isSignupRoute() {
  const params = new URLSearchParams(window.location.search);
  return params.has("signup") || window.location.hash === "#signup";
}

function isOnboardingCompleteRoute() {
  const params = new URLSearchParams(window.location.search);
  return params.has("onboarding_complete");
}

function AppRouter() {
  const { user, profile, loading, signIn, signOut } = useAuth();

  // School self-serve signup state
  const [showSignup, setShowSignup] = useState(isSignupRoute);
  const [onboardingData, setOnboardingData] = useState(
    // If returning from Stripe, restore onboarding data from sessionStorage
    isOnboardingCompleteRoute()
      ? JSON.parse(sessionStorage.getItem("relay_onboarding") || "null")
      : null
  );

  // Show self-serve signup page (public, no auth required)
  if (showSignup && !user) {
    return (
      <SchoolSignup
        onSuccess={async (data) => {
          // Persist onboarding data (survives Stripe redirect)
          const { password, ...onboardingInfo } = data;
          sessionStorage.setItem("relay_onboarding", JSON.stringify(onboardingInfo));
          setOnboardingData(onboardingInfo);
          setShowSignup(false);
          // Auto-login with the credentials just used to register
          try {
            await signIn(data.adminEmail, password);
          } catch (err) {
            console.error("Auto-login after signup failed:", err);
          }
        }}
        onGoToLogin={() => setShowSignup(false)}
      />
    );
  }

  // Show onboarding wizard after successful signup (or after Stripe return)
  if (onboardingData && user) {
    return (
      <SchoolOnboarding
        schoolSlug={onboardingData.schoolSlug}
        schoolName={onboardingData.schoolName}
        adminName={onboardingData.adminName}
        onEnterDashboard={() => {
          sessionStorage.removeItem("relay_onboarding");
          setOnboardingData(null);
          // Clean up URL params
          window.history.replaceState({}, "", window.location.pathname);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div style={{
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        backgroundColor: COLORS.bg, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
          }}>R</div>
          <p style={{ color: COLORS.textMuted, fontSize: 14 }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  if (!profile) {
    return (
      <div style={{
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        backgroundColor: COLORS.bg, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
          }}>R</div>
          <p style={{ color: COLORS.textMuted, fontSize: 14 }}>Loading profile...</p>
        </div>
      </div>
    );
  }

  // Role gate: only careers and admin roles can access
  if (profile.role !== "careers" && profile.role !== "admin") {
    return (
      <div style={{
        fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
        backgroundColor: COLORS.bg, minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={{
          textAlign: "center",
          backgroundColor: COLORS.surface,
          borderRadius: 12,
          border: `1px solid ${COLORS.border}`,
          padding: 40,
          maxWidth: 400,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.red,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
          }}>!</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>Access Denied</h2>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, marginBottom: 20 }}>
            This dashboard is for careers team members only. Your account role is "{profile.role}".
          </p>
          <button onClick={signOut} style={{
            padding: "10px 24px", borderRadius: 8, border: "none",
            backgroundColor: COLORS.accent, color: "#fff", fontSize: 14,
            fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
          }}>
            Log Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppShell />
      <DemoSwitcher />
    </>
  );
}

function ErrorFallback({ resetError }) {
  return (
    <div style={{
      fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
      backgroundColor: COLORS.bg, minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{ textAlign: "center", maxWidth: 400 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, backgroundColor: "#E5484D",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 16,
        }}>!</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
          Something went wrong
        </h2>
        <p style={{ fontSize: 14, color: COLORS.textSecondary, marginBottom: 20 }}>
          An unexpected error occurred. Our team has been notified.
        </p>
        <button onClick={resetError} style={{
          padding: "10px 24px", borderRadius: 8, border: "none",
          backgroundColor: COLORS.accent, color: "#fff", fontSize: 14,
          fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
        }}>
          Try Again
        </button>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}
